// Import polyfills first for Android 8 compatibility
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client'; // Use createRoot (React 18+)
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as Sentry from '@sentry/react';

// Polyfill for globalThis (required for React 19 compatibility)
// This ensures globalThis is available before any React code executes
if (typeof globalThis === 'undefined') {
  (function() {
    'use strict';
    var getGlobal = function() {
      // eslint-disable-next-line no-restricted-globals
      if (typeof self !== 'undefined') { return self; }
      if (typeof window !== 'undefined') { return window; }
      if (typeof global !== 'undefined') { return global; }
      throw new Error('unable to locate global object');
    };
    var g = getGlobal();
    if (!g.globalThis) {
      g.globalThis = g;
    }
  })();
}

// Initialize Sentry with error handling for older browsers
try {
  if (process.env.REACT_APP_SENTRY_DNS) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DNS,
      // This enables automatic instrumentation (highly recommended), but is not
      // necessary for purely manual usage
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: 0.2,
    });
  }
} catch (sentryError) {
  console.warn('Sentry initialization failed (may not be supported on this browser):', sentryError);
}

// Error boundary wrapper component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      const errorMessage = (error && error.message) ? error.message : 'Unknown error';
      const errorStack = (error && error.stack) ? error.stack : '';
      
      return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
          <h1>Something went wrong</h1>
          <p>Error: {errorMessage}</p>
          <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto' }}>
            {errorStack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create a root and render the app
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}


// Detect Android 8 or older WebView
const isAndroid8OrOlder = () => {
  const userAgent = navigator.userAgent || '';
  const androidMatch = userAgent.match(/Android\s+([0-9]+)/);
  if (androidMatch) {
    const androidVersion = parseInt(androidMatch[1], 10);
    return androidVersion <= 8;
  }
  return false;
};


// Create app content with error boundary
let appContent;
try {
  appContent = (
    <StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StrictMode>
  );
} catch (error) {
  const errorMsg = 'Failed to create app content: ' + (error.message || String(error));
  console.error(errorMsg, error);
  // Create a simple error component
  appContent = React.createElement('div', {
    style: { padding: '20px', fontFamily: 'Arial, sans-serif' }
  }, [
    React.createElement('h1', { key: 'h1' }, 'Error Creating App'),
    React.createElement('p', { key: 'p' }, errorMsg)
  ]);
}

// Render the app using createRoot (React 19 requires this)
const renderApp = () => {
  try {
    // React 19 only supports createRoot - ReactDOM.render was removed
    const root = createRoot(rootElement);
    root.render(appContent);
    console.log('Application rendered successfully using createRoot');
  } catch (error) {
    const errorMsg = 'Failed to render application: ' + (error.message || String(error));
    console.error(errorMsg, error);
    
    // Show user-friendly error message
    const isOldBrowser = isAndroid8OrOlder();
    const browserMessage = isOldBrowser 
      ? '<p>Your browser version is too old. Please update your browser or use a different device.</p>'
      : '<p>An error occurred while loading the application. Please try refreshing the page.</p>';
    
    rootElement.innerHTML = '<div style="padding: 20px; font-family: Arial, sans-serif;"><h1>Browser Not Supported</h1>' + 
      browserMessage + 
      '<p style="color: red; font-size: 12px;">Error: ' + (error.message || String(error)) + '</p></div>';
  }
};

// Add global error handler
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error, event.message, event.filename, event.lineno);
  // If root element is still empty, show error message
  if (rootElement && !rootElement.hasChildNodes()) {
    rootElement.innerHTML = '<div style="padding: 20px; font-family: Arial, sans-serif;"><h1>Application Error</h1><p>An error occurred while loading the application. Please try refreshing the page.</p><p style="color: #666; font-size: 12px;">Error: ' + (event.message || 'Unknown error') + '</p></div>';
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});

// Render the app
renderApp();

try {
  localStorage.setItem('REACT_APP_SENTRY_DNS', process.env.REACT_APP_SENTRY_DNS);
} catch (e) {
  console.error('Some issue encountered in setting REACT_APP_SENTRY_DNS:', e);
}

// Service worker configuration
serviceWorker.unregister();